import type { SearchSection } from '@setplex/tria-api'
import type { MotnItem } from '~/entities/motn'

import { combine, createEvent, createStore, sample } from 'effector'
import { createAction } from 'effector-action'
import { model as i18n } from '~/core/i18n'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { motnOpenDetails, motnOpenStreamingOptions } from '~/features/analytix'
import {
  closeMotnStreamingOptionsPopup,
  openMotnStreamingOptionsPopup,
} from '~/widgets/Popups/MotnStreamingOptionsPopup/model'
import { isMotnMovie, parseMotnItemFromHash } from './lib'

export const init = createEvent<{ query: URLSearchParams }>()
export const left = createEvent()

/**
 * Search MOTN results already emit ET-1 via `handleMotnClick` (FP-4242).
 * Skip the FP-3927 poster-tap so the same open is not counted twice.
 * Matches `EXTERNAL_SECTION` in `routes/search/hybrid/analytics.ts`.
 */
const SEARCH_MOTN_ROUTE_NAME: SearchSection = 'EXTERNAL'

export const $motnItem = createStore<MotnItem | null>(null) //
  .reset(left)

const $labels = combine($motnItem, i18n.$t, (item, { t }) => {
  if (!item) return []
  if (isMotnMovie(item)) {
    return [item.year, item.length]
  }
  const seasonLabel = item.seasonCount
    ? t('season_count', { count: item.seasonCount })
    : undefined
  const episodeLabel = item.episodeCount
    ? t('episode_count', { count: item.episodeCount })
    : undefined
  return [item.year, seasonLabel, episodeLabel]
})

createAction(init, {
  source: router.$hash,
  target: {
    $motnItem,
    redirect: router.navigateReplace,
    detailsOpened: motnOpenDetails,
  },
  fn(target, hash, { query }) {
    const item = parseMotnItemFromHash(hash)
    if (item) {
      target.$motnItem(item)
      if (query.get('routeName') !== SEARCH_MOTN_ROUTE_NAME) {
        target.detailsOpened(item)
      }
    } else {
      target.redirect('/')
    }
  },
})

const whereToWatchClick = createEvent()

createAction(whereToWatchClick, {
  source: $motnItem,
  target: {
    popup: openMotnStreamingOptionsPopup,
    optionsShowed: motnOpenStreamingOptions,
  },
  fn(target, item) {
    if (!item || !item.streamingOptions?.length) return
    target.popup({ item, options: item.streamingOptions })
    target.optionsShowed(item)
  },
})

sample({
  clock: left,
  target: closeMotnStreamingOptionsPopup,
})

register($motnItem, '$routes/motn/details/motnItem')
register($labels, '$routes/motn/details/labels')
register(whereToWatchClick, 'routes/motn/details/whereToWatchClick')
